import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../Config";

import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";

const MantenedorProveedores = () => {
  const { baseUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  const [proveedores, setProveedores] = useState([]);
  const [tiposProveedor, setTiposProveedor] = useState([]); // [{id, descripcion}]
  const [lastLoadAt, setLastLoadAt] = useState(null);

  // --- Crear ---
  const [createOpen, setCreateOpen] = useState(false);
  const [createTipoId, setCreateTipoId] = useState(null);
  const [createRut, setCreateRut] = useState("");
  const [createRazonSocial, setCreateRazonSocial] = useState("");

  // --- Editar ---
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editTipoId, setEditTipoId] = useState(null);
  const [editRut, setEditRut] = useState("");
  const [editRazonSocial, setEditRazonSocial] = useState("");

  // --- Config ---
  const [configOpen, setConfigOpen] = useState(false);
  const [configRow, setConfigRow] = useState(null);
  const [configJsonText, setConfigJsonText] = useState("");
  const [configMode, setConfigMode] = useState("view"); // view|upload

  // --- Validar RUT ---
  const [createRutError, setCreateRutError] = useState("");
  const [editRutError, setEditRutError] = useState("");

  const [createMail, setCreateMail] = useState("");
  const [createTelefono, setCreateTelefono] = useState("");

  const [editMail, setEditMail] = useState("");
  const [editTelefono, setEditTelefono] = useState("");

  const joinUrl = (base, path) =>
    `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(
      /^\/+/,
      "",
    )}`;

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const safePrettyJson = (raw) => {
    if (raw == null) return "";
    if (typeof raw === "string") {
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        return raw;
      }
    }
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  };

  const parseJsonOrThrow = (text) => JSON.stringify(JSON.parse(text));

  // ---- Cargar tipos proveedor ----
  const cargarTiposProveedor = async () => {
    try {
      const url = joinUrl(baseUrl, "tipoProveedor/buscar");
      const data = await fetchJson(url, { method: "GET" });
      const tipos = Array.isArray(data?.tipos) ? data.tipos : [];
      setTiposProveedor(tipos);
    } catch (e) {
      console.error(e);
      // No bloqueo la página, pero aviso
      setTiposProveedor([]);
      alert("No se pudieron cargar tipos de proveedor: " + (e?.message || e));
    }
  };

  // ---- Cargar proveedores + config activa ----
  const cargarProveedores = async () => {
    setIsLoading(true);
    try {
      const url = joinUrl(baseUrl, "proveedor/buscar/con-resumen");
      const data = await fetchJson(url, { method: "GET" });
      const rows = Array.isArray(data?.proveedores) ? data.proveedores : [];

      const enriched = await Promise.all(
        rows.map(async (p) => {
          const id = p?.id;
          if (!id) return { ...p, extraccionConfigActiva: null };
          try {
            const cfgUrl =
              joinUrl(baseUrl, "proveedor/extraccion-config/activa") +
              `?idProveedor=${encodeURIComponent(id)}`;
            const cfgData = await fetchJson(cfgUrl, { method: "GET" });
            return {
              ...p,
              extraccionConfigActiva: cfgData?.extraccionConfigActiva ?? null,
            };
          } catch {
            return { ...p, extraccionConfigActiva: null };
          }
        }),
      );

      setProveedores(enriched);
      setLastLoadAt(new Date());
    } catch (e) {
      console.error(e);
      alert("Error cargando proveedores: " + (e?.message || e));
      setProveedores([]);
      setLastLoadAt(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cargar ambos al inicio
    cargarTiposProveedor();
    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Crear proveedor ----
  const openCreate = () => {
    setCreateTipoId(null);
    setCreateRut("");
    setCreateRazonSocial("");
    setCreateRutError("");
    setCreateOpen(true);
    setCreateMail("");
    setCreateTelefono("");
  };

  const closeCreate = () => setCreateOpen(false);

  const crearProveedor = async () => {
    if (!createTipoId) return alert("Debe seleccionar tipo de proveedor.");
    if (!String(createRut || "").trim()) return alert("Debe ingresar RUT.");
    if (!String(createRazonSocial || "").trim())
      return alert("Debe ingresar Razón Social.");

    const err = validateCreateRut(createRut);
    setCreateRutError(err);
    if (err) return;

    setIsLoading(true);
    try {
      const payload = {
        idTipoProveedor: createTipoId,
        rut: createRut.trim(),
        razonSocial: createRazonSocial.trim(),
        mail: createMail.trim(),
        telefono: createTelefono.trim(),
      };

      await fetchJson(joinUrl(baseUrl, "proveedor/insertar"), {
        method: "POST",
        body: JSON.stringify(payload),
      });

      await cargarProveedores();
      closeCreate();
      alert("Proveedor creado correctamente.");
    } catch (e) {
      console.error(e);
      alert("Error creando proveedor: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Editar proveedor ----
  const openEdit = (row) => {
    setEditRow(row);
    setEditTipoId(row?.tipoProveedor?.id ?? null);
    setEditRut(row?.rut ?? "");
    setEditRazonSocial(row?.razonSocial ?? "");
    setEditRutError(validateEditRut(row?.rut ?? ""));
    setEditMail(row?.mail ?? "");
    setEditTelefono(row?.telefono ?? "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditTipoId(null);
    setEditRut("");
    setEditRazonSocial("");
    setEditMail("");
    setEditTelefono("");
  };

  const guardarEdicion = async () => {
    if (!editRow?.id) return alert("Proveedor inválido.");
    if (!editTipoId) return alert("Debe seleccionar tipo de proveedor.");
    if (!String(editRut || "").trim()) return alert("Debe indicar RUT.");
    if (!String(editRazonSocial || "").trim())
      return alert("Debe indicar Razón Social.");

    const err = validateEditRut(editRut);
    setEditRutError(err);
    if (err) return;

    setIsLoading(true);
    try {
      const payload = {
        id: editRow.id,
        idTipoProveedor: editTipoId,
        rut: editRut.trim(),
        razonSocial: editRazonSocial.trim(),
        mail: editMail.trim(),
        telefono: editTelefono.trim(),
      };

      await fetchJson(joinUrl(baseUrl, "proveedor/actualizar"), {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      await cargarProveedores();
      closeEdit();
      alert("Proveedor actualizado correctamente.");
    } catch (e) {
      console.error(e);
      alert("Error actualizando proveedor: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Config ----
  const openConfigView = (row) => {
    setConfigRow(row);
    setConfigMode("view");

    const raw = row?.extraccionConfigActiva?.configJson;
    if (!raw) {
      setConfigJsonText("// Este proveedor NO tiene configuración activa.");
    } else {
      setConfigJsonText(safePrettyJson(raw));
    }

    setConfigOpen(true);
  };

  const openConfigUpload = (row) => {
    setConfigRow(row);
    setConfigMode("upload");
    setConfigJsonText(safePrettyJson(row?.extraccionConfigActiva?.configJson));
    setConfigOpen(true);
  };

  const closeConfig = () => {
    setConfigOpen(false);
    setConfigRow(null);
    setConfigJsonText("");
    setConfigMode("view");
  };

  const subirConfig = async () => {
    if (!configRow?.id) return alert("Proveedor inválido.");
    if (!String(configJsonText || "").trim())
      return alert("Debe pegar un JSON.");

    let jsonCompact;
    try {
      jsonCompact = parseJsonOrThrow(configJsonText);
    } catch (e) {
      return alert("El JSON no es válido: " + (e?.message || e));
    }

    setIsLoading(true);
    try {
      const payload = { proveedorId: configRow.id, configJson: jsonCompact };

      await fetchJson(joinUrl(baseUrl, "proveedor/extraccion-config/guardar"), {
        method: "POST",
        body: JSON.stringify(payload),
      });

      await cargarProveedores();
      closeConfig();
      alert("Configuración guardada correctamente.");
    } catch (e) {
      console.error(e);
      alert("Error guardando configuración: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  // ---- UI helpers ----
  const tipoOptions = useMemo(
    () =>
      (tiposProveedor || []).map((t) => ({
        label: t.descripcion,
        value: t.id,
      })),
    [tiposProveedor],
  );

  const renderTipoProveedor = (row) => {
    const desc = row?.tipoProveedor?.descripcion;
    const id = row?.tipoProveedor?.id;
    if (desc && id != null) return `${desc} (ID ${id})`;
    if (desc) return desc;
    if (id != null) return `ID ${id}`;
    return "—";
  };

  const renderConfigBadge = (row) => {
    const cfg = row?.extraccionConfigActiva;
    if (!cfg) return <Tag value="Sin config" severity="warning" />;
    const v = cfg?.version ?? "—";
    return <Tag value={`Activa v${v}`} severity="success" />;
  };

  const renderMedidoresActivos = (row) => {
    const cantidad = row?.cantidadMedidoresActivos ?? 0;

    return (
      <Tag
        value={`${cantidad}`}
        severity={cantidad > 0 ? "success" : "secondary"}
      />
    );
  };

  const renderAcciones = (row) => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <Button
        icon="pi pi-pencil"
        label="Editar"
        className="p-button-sm"
        onClick={() => openEdit(row)}
        disabled={isLoading}
      />
      <Button
        icon="pi pi-eye"
        label="Config"
        className="p-button-sm p-button-secondary"
        onClick={() => openConfigView(row)}
        disabled={isLoading}
      />
      <Button
        icon="pi pi-upload"
        label="Subir config"
        className="p-button-sm p-button-help"
        onClick={() => openConfigUpload(row)}
        disabled={isLoading}
      />
    </div>
  );

  const headerCard = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>
            Mantenedor de Proveedores
          </h4>
          <small style={{ color: "#6c757d" }}>
            Ver, crear, editar proveedores y gestionar configuración de
            extracción
          </small>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            label="Nuevo proveedor"
            icon="pi pi-plus"
            className="p-button-sm p-button-success"
            onClick={openCreate}
            disabled={isLoading}
          />
          <Button
            label="Refrescar"
            icon="pi pi-refresh"
            className="p-button-sm"
            onClick={() => {
              cargarTiposProveedor();
              cargarProveedores();
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    ),
    [isLoading],
  );

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20, 50],
    stripedRows: true,
    showGridlines: true,
  };

  // --- RUT Chile helpers ---

  const normalizeRut = (rut) =>
    String(rut || "")
      .trim()
      .toUpperCase()
      .replace(/\./g, "")
      .replace(/[^0-9K-]/g, ""); // deja 0-9, K y guion

  const formatRut = (rut) => {
    const n = normalizeRut(rut).replace(/-/g, "");
    if (n.length < 2) return rut || "";
    const body = n.slice(0, -1);
    const dv = n.slice(-1);
    const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${withDots}-${dv}`;
  };

  const computeRutDv = (bodyDigits) => {
    let sum = 0;
    let mul = 2;
    for (let i = bodyDigits.length - 1; i >= 0; i--) {
      sum += Number(bodyDigits[i]) * mul;
      mul = mul === 7 ? 2 : mul + 1;
    }
    const mod = 11 - (sum % 11);
    if (mod === 11) return "0";
    if (mod === 10) return "K";
    return String(mod);
  };

  const isValidRut = (rut) => {
    const r = normalizeRut(rut);
    const parts = r.includes("-")
      ? r.split("-")
      : [r.slice(0, -1), r.slice(-1)];
    const body = (parts[0] || "").replace(/\D/g, "");
    const dv = String(parts[1] || "").toUpperCase();

    if (!body || body.length < 7 || body.length > 8) return false;
    if (!dv || !/^[0-9K]$/.test(dv)) return false;

    return computeRutDv(body) === dv;
  };

  const validateCreateRut = (value) => {
    const v = String(value || "").trim();
    if (!v) return "Debe ingresar RUT.";
    if (!isValidRut(v)) return "RUT no válido.";
    return "";
  };

  const validateEditRut = (value) => {
    const v = String(value || "").trim();
    if (!v) return "Debe ingresar RUT.";
    if (!isValidRut(v)) return "RUT no válido.";
    return "";
  };

  return (
    <div className="col-12 md:col-12">
      <div
        style={{
          padding: 20,
          backgroundColor: "#f8f9fa",
          borderRadius: 8,
          boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          marginBottom: 20,
        }}
      >
        {headerCard}
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      <div
        style={{
          padding: 20,
          backgroundColor: "#fff",
          borderRadius: 8,
          boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
          marginBottom: 20,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <strong>Proveedores</strong>
          <div style={{ color: "#6c757d", fontSize: 13 }}>
            {lastLoadAt ? `Última carga: ${lastLoadAt.toLocaleString()}` : "—"}
            {" · "}
            {proveedores.length} registros
          </div>
        </div>

        <DataTable value={proveedores} dataKey="id" {...tablaProps}>
          <Column field="id" header="ID" sortable style={{ width: 90 }} />
          <Column header="Tipo proveedor" body={renderTipoProveedor} sortable />
          <Column field="rut" header="RUT" sortable />
          <Column field="razonSocial" header="Razón Social" sortable />
          <Column field="mail" header="Mail" sortable />
          <Column field="telefono" header="Teléfono" sortable />
          <Column
            header="Medidores activos"
            body={renderMedidoresActivos}
            sortable
            sortField="cantidadMedidoresActivos"
            style={{ width: 160, textAlign: "center" }}
          />
          <Column
            header="Config extracción"
            body={(row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {renderConfigBadge(row)}
              </div>
            )}
          />
          <Column
            header="Acciones"
            body={renderAcciones}
            style={{ minWidth: 340 }}
          />
        </DataTable>
      </div>

      {/* ===== Dialog Crear ===== */}
      <Dialog
        header="Nuevo proveedor"
        visible={createOpen}
        style={{ width: "720px", maxWidth: "95vw" }}
        modal
        onHide={closeCreate}
        footer={
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={closeCreate}
              disabled={isLoading}
            />
            <Button
              label="Crear"
              icon="pi pi-save"
              className="p-button-success"
              onClick={crearProveedor}
              disabled={
                isLoading ||
                !!createRutError ||
                !createTipoId ||
                !String(createRut || "").trim() ||
                !String(createRazonSocial || "").trim()
              }
            />
          </div>
        }
      >
        <div className="grid">
          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Tipo proveedor</label>
            <Dropdown
              value={createTipoId}
              options={tipoOptions}
              onChange={(e) => setCreateTipoId(e.value)}
              placeholder="Seleccione tipo"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
              filter
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>RUT</label>
            <InputText
              value={createRut}
              onChange={(e) => {
                const v = e.target.value;
                setCreateRut(v);
                setCreateRutError(validateCreateRut(v));
              }}
              onBlur={() => {
                // opcional: formatear al salir
                if (isValidRut(createRut)) setCreateRut(formatRut(createRut));
                setCreateRutError(validateCreateRut(createRut));
              }}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="12.345.678-5"
              className={createRutError ? "p-invalid" : ""}
            />
            {createRutError && (
              <small style={{ color: "#dc3545" }}>{createRutError}</small>
            )}
          </div>

          <div className="col-12 md:col-12">
            <label style={{ fontWeight: "bold" }}>Razón Social</label>
            <InputText
              value={createRazonSocial}
              onChange={(e) => setCreateRazonSocial(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="Nombre proveedor"
            />
          </div>
          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Mail</label>
            <InputText
              value={createMail}
              onChange={(e) => setCreateMail(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="correo@proveedor.cl"
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Teléfono</label>
            <InputText
              value={createTelefono}
              onChange={(e) => setCreateTelefono(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="+56 9 1234 5678"
            />
          </div>
        </div>
      </Dialog>

      {/* ===== Dialog Editar ===== */}
      <Dialog
        header="Editar proveedor"
        visible={editOpen}
        style={{ width: "720px", maxWidth: "95vw" }}
        modal
        onHide={closeEdit}
        footer={
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={closeEdit}
              disabled={isLoading}
            />
            <Button
              label="Guardar"
              icon="pi pi-save"
              onClick={guardarEdicion}
              disabled={
                isLoading ||
                !!editRutError ||
                !editTipoId ||
                !String(editRut || "").trim() ||
                !String(editRazonSocial || "").trim()
              }
            />
          </div>
        }
      >
        <div className="grid">
          <div className="col-12 md:col-9">
            <label style={{ fontWeight: "bold" }}>Tipo proveedor</label>
            <Dropdown
              value={editTipoId}
              options={tipoOptions}
              onChange={(e) => setEditTipoId(e.value)}
              placeholder="Seleccione tipo"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
              filter
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>RUT</label>
            <InputText
              value={editRut}
              onChange={(e) => {
                const v = e.target.value;
                setEditRut(v);
                setEditRutError(validateEditRut(v));
              }}
              onBlur={() => {
                if (isValidRut(editRut)) setEditRut(formatRut(editRut));
                setEditRutError(validateEditRut(editRut));
              }}
              style={{ width: "100%" }}
              disabled={isLoading}
              className={editRutError ? "p-invalid" : ""}
            />
            {editRutError && (
              <small style={{ color: "#dc3545" }}>{editRutError}</small>
            )}
          </div>

          <div className="col-12 md:col-12">
            <label style={{ fontWeight: "bold" }}>Razón Social</label>
            <InputText
              value={editRazonSocial}
              onChange={(e) => setEditRazonSocial(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
            />
          </div>
          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Mail</label>
            <InputText
              value={editMail}
              onChange={(e) => setEditMail(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="correo@proveedor.cl"
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Teléfono</label>
            <InputText
              value={editTelefono}
              onChange={(e) => setEditTelefono(e.target.value)}
              style={{ width: "100%" }}
              disabled={isLoading}
              placeholder="+56 9 1234 5678"
            />
          </div>
        </div>
      </Dialog>

      {/* ===== Dialog Config ===== */}
      <Dialog
        header={
          configMode === "upload"
            ? "Subir configuración de extracción"
            : "Config activa"
        }
        visible={configOpen}
        style={{ width: "900px", maxWidth: "98vw" }}
        contentStyle={{ maxHeight: "75vh", overflow: "auto" }}
        modal
        onHide={closeConfig}
        footer={
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button
              label="Cerrar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={closeConfig}
              disabled={isLoading}
            />
            {configMode === "upload" && (
              <Button
                label="Guardar config"
                icon="pi pi-save"
                className="p-button-success"
                onClick={subirConfig}
                disabled={isLoading}
              />
            )}
          </div>
        }
      >
        {/** ✅ detectar si hay config */}
        {(() => {
          const hasConfig = !!configRow?.extraccionConfigActiva?.configJson;

          return (
            <>
              <div style={{ marginBottom: 10, color: "#6c757d" }}>
                <div>
                  <strong>Proveedor:</strong>{" "}
                  {configRow
                    ? `${configRow?.razonSocial ?? "—"} (ID ${
                        configRow?.id ?? "—"
                      })`
                    : "—"}
                </div>
                <div>
                  <strong>Tipo:</strong>{" "}
                  {configRow?.tipoProveedor?.descripcion || "—"}
                </div>
                <div>
                  <strong>Estado:</strong>{" "}
                  {configRow?.extraccionConfigActiva
                    ? `Activa v${
                        configRow.extraccionConfigActiva?.version ?? "—"
                      }`
                    : "Sin configuración activa"}
                </div>
              </div>

              {/** ✅ Mensaje cuando no hay config (solo vista) */}
              {configMode === "view" && !hasConfig && (
                <div
                  style={{
                    padding: 12,
                    background: "#fff3cd",
                    border: "1px solid #ffeeba",
                    borderRadius: 8,
                    marginBottom: 12,
                    color: "#856404",
                  }}
                >
                  <strong>Sin configuración:</strong> este proveedor no tiene
                  configuración activa.
                </div>
              )}

              <label style={{ fontWeight: "bold" }}>configJson</label>

              <InputTextarea
                value={
                  configMode === "view" && !hasConfig
                    ? "// Este proveedor NO tiene configuración activa."
                    : configJsonText || ""
                }
                onChange={(e) => setConfigJsonText(e.target.value)}
                rows={18}
                style={{
                  width: "100%",
                  height: "50vh",
                  overflow: "auto",
                  fontFamily: "monospace",
                  whiteSpace: "pre",
                }}
                readOnly={configMode === "view"}
                disabled={isLoading}
                placeholder='{"Consulte con el administrador el formato JSON de la configuración."}'
              />
            </>
          );
        })()}
      </Dialog>
    </div>
  );
};

export default MantenedorProveedores;
